import { AppContextService } from '../../../services/app-context.service';
import { ActivatedRoute as ContextRoute } from '@angular/router';
import { Optional as ContextOptional, ElementRef as ContextElement } from '@angular/core';
import { Subscription as ContextSubscription } from 'rxjs';
import { viewContextKey, viewContextVisible } from '../../../shared/view-context';
import { DataListComponent } from '../../../shared/ui/data-list/data-list.component';
import { DataPage, DataQuery } from '../../../models/data-source';
import { Component, TemplateRef, ViewChild } from '@angular/core';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { DataTableColumn, DataTableComponent } from '../../../shared/ui/data-table/data-table.component';
import { AuditLogService, AuditLogDto, AuditLogFilter } from './audit-log.service';

@Component({
  selector: 'app-audit-log',
  templateUrl: './audit-log.component.html',
})
export class AuditLogComponent {
  @ViewChild('compactList') compactList?: DataListComponent<AuditLogDto>;
  collectionSearch = '';

  readonly categories = ['Security', 'Financial', 'Operational', 'Config'];

  /** A column id is its server sort key; the server allowlists what it accepts. */
  readonly columns: DataTableColumn[] = [
    { id: 'createdAt', label: 'Time (UTC)' },
    { id: 'category',  label: 'Category' },
    { id: 'message',   label: 'Event' },
    { id: 'userEmail', label: 'User' },
    { id: 'ipAddress', label: 'IP' },
  ];

  filter: AuditLogFilter = { category: '', from: '', to: '', userId: null };
  @ViewChild('table') table?: DataTableComponent<AuditLogDto>;
  @ViewChild('detailDialog', { static: true }) detailDialog!: TemplateRef<AuditLogDto>;

  private detailDialogRef?: MatDialogRef<unknown>;

  private viewRegistration?: ContextSubscription;
  private registerViewContext(): void {
    this.viewRegistration = this.viewContext?.registerView({
      key: viewContextKey(this.viewRoute, 'Audit Log filters'), name: 'Audit Log filters', priority: -10,
      active: () => viewContextVisible(this.viewElement),
      read: () => (this.filter),
      describe: state => JSON.stringify(state),
      restore: state => { this.filter = state; this.load(); },
    });
  }

  ngOnDestroy(): void { this.viewRegistration?.unsubscribe(); }

  ngOnInit(): void { this.registerViewContext(); }

  constructor(private auditLogService: AuditLogService, private dialog: MatDialog,
    @ContextOptional() private readonly viewContext?: AppContextService,
    @ContextOptional() private readonly viewRoute?: ContextRoute,
    @ContextOptional() private readonly viewElement?: ContextElement<HTMLElement>,
  ) {}

  /**
   * Server-mode source. Reads the filter bar at call time, so applying a
   * filter only has to ask the table to reload.
   *
   * This log grows without bound and is never truncated, so every query has
   * to reach the database - there is no version of this page that holds the
   * whole table in a browser.
   */
  fetch = (q: DataQuery): Observable<DataPage<AuditLogDto>> =>
    this.auditLogService.get(this.filter, {
      search: q.search || undefined,
      sort: q.sort || undefined,
      desc: q.desc,
      page: q.page,
      pageSize: q.pageSize,
    }).pipe(map(p => ({ items: p.rows, total: p.total })));

  load(): void {
    this.table?.reload();
    this.compactList?.reload();
  }

  /**
   * Close when the view replaces its results. Appending cards does not emit
   * queryChange, so background loading cannot dismiss the inspected record.
   */
  onQueryChange(): void { this.detailDialogRef?.close(); }

  showDetail(row: AuditLogDto): void {
    this.detailDialogRef?.close();
    const ref = this.dialog.open(this.detailDialog, {
      data: row,
      width: '44rem',
      maxWidth: 'calc(100vw - 2rem)',
    });
    this.detailDialogRef = ref;
    ref.afterClosed().subscribe(() => {
      if (this.detailDialogRef === ref) this.detailDialogRef = undefined;
    });
  }
}
